/* eslint-disable no-console */
import { action, runInAction, observable } from 'mobx';
import { socketService } from './socketService';
import { messageActions } from './messageStore';
import { PushMessage } from '../api/socket.types';
// import { toastr } from 'react-redux-toastr';

// Новые типы (уже у вас есть, но для контекста)
export interface ContentType {
  id?: string;
  eventType?: string;
  invoiceId?: string;
  orderStatusRefValueId?: number;
  orderId?: string;
  messageText?: string;
  // Другие поля будут автоматически включены при расширении интерфейса
}

export interface SocketStore {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  logs: string[];
}

const initialValues: SocketStore = {
  isConnected: false,
  isConnecting: false,
  error: null,
  logs: [],
};

export const socketStore = observable(initialValues);

// Перехват console.log для логирования
const originalConsoleLog = console.log;
console.log = (...args: any[]) => {
  originalConsoleLog.apply(console, args);
  const logString = args.map(arg =>
    typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
  ).join(' ');

  runInAction(() => {
    socketStore.logs = [...socketStore.logs.slice(-100), logString];
  });
};

// Вспомогательная функция: безопасно преобразует любые входные данные в ContentType
function safeConvertToContentType(input: unknown): ContentType {
  // Случай 1: строка → messageText
  if (typeof input === 'string') {
    return { messageText: input };
  }

  // Случай 2: null или undefined → пустой объект
  if (input == null) {
    return {};
  }

  // Случай 3: объект → копируем все поля, но гарантируем, что это plain object
  if (typeof input === 'object' && !Array.isArray(input)) {
    try {
      // Создаём новый объект, чтобы избежать мутаций и прототипов
      const cleanObj: Record<string, unknown> = {};
      for (const key in input) {
        if (Object.prototype.hasOwnProperty.call(input, key)) {
          cleanObj[key] = input[key as keyof typeof input];
        }
      }
      return cleanObj as ContentType; // TypeScript доверяет вам — вы сами расширяете ContentType
    } catch (e) {
      console.warn('Failed to parse object content, falling back to messageText', e);
      return { messageText: JSON.stringify(input, null, 2) };
    }
  }

  // Случай 4: массив, число, boolean и т.п. → сериализуем в messageText
  return { messageText: JSON.stringify(input, null, 2) };
}

// Callbacks для событий Socket
const socketEventCallbacks = {
  onConnectionStatus: action((status: boolean) => {
    socketStore.isConnected = status;
    socketStore.isConnecting = false;
  }),

  onConnectionError: action((error: any) => {
    socketStore.error = error?.message || String(error);
    socketStore.isConnecting = false;
  }),

  onPushReceived: action((data: { message: PushMessage; callback?: (ack: string) => void }) => {
    try {
      const contentObj = safeConvertToContentType(data.message);

      // Для toastr по-прежнему используем текстовое представление
      // let toastText = 'New notification';
      // if (typeof data.message === 'object' && data.message !== null) {
      //   if ('text' in data.message && typeof data.message.text === 'string') {
      //     toastText = data.message.text;
      //   } else if ('body' in data.message && typeof data.message.body === 'string') {
      //     toastText = data.message.body;
      //   } else if ('content' in data.message && typeof data.message.content === 'string') {
      //     toastText = data.message.content;
      //   } else {
      //     toastText = JSON.stringify(data.message, null, 2);
      //   }
      // } else if (typeof data.message === 'string') {
      //   toastText = data.message;
      // }

      // toastr.success('Уведомление ℹ️', toastText);

      messageActions.addMessage({
        id: Date.now().toString(),
        content: contentObj,
        timestamp: new Date(),
        sender: 'system',
        type: 'push',
      });
    } catch (error) {
      console.error('Error processing push message:', error, data);
      messageActions.addMessage({
        id: Date.now().toString(),
        content: { messageText: 'Received push message (format error)' },
        timestamp: new Date(),
        sender: 'system',
        type: 'push',
      });
    }
  }),

  onHelloReceived: action((data: { message: string; callback?: (ack: string) => void }) => {
    messageActions.addMessage({
      id: Date.now().toString(),
      content: { messageText: data.message },
      timestamp: new Date(),
      sender: 'system',
      type: 'hello',
    });
    data.callback?.('acknowledged');
  }),

  onEventReceived: action((payload: { data: Record<string, unknown>; callback?: (ack: string) => void }) => {
    const contentObj = safeConvertToContentType(payload.data);

    messageActions.addMessage({
      id: Date.now().toString(),
      content: contentObj,
      timestamp: new Date(),
      sender: 'system',
      type: 'event',
    });

    payload.callback?.('acknowledged');
  }),
};

// Подписка на события
socketService.on('connection:status', socketEventCallbacks.onConnectionStatus);
socketService.on('connection:error', socketEventCallbacks.onConnectionError);
socketService.on('push', socketEventCallbacks.onPushReceived);
// socketService.on('hello', socketEventCallbacks.onHelloReceived); // закомментировано у вас — оставим так
socketService.on('event', socketEventCallbacks.onEventReceived);

export const socketActions = {
  connect: action(async (url?: string) => {
    try {
      socketStore.isConnecting = true;
      socketStore.error = null;
      if (url) {
        socketService.setUrl(url);
      }
      socketService.connect();
    } catch (error) {
      runInAction(() => {
        socketStore.isConnecting = false;
        socketStore.isConnected = false;
        socketStore.error = (error as Error).message;
      });
      console.error('Socket connection error:', error);
    }
  }),

  disconnect: action(() => {
    socketService.disconnect();
    socketStore.isConnected = false;
    socketStore.isConnecting = false;
  }),

  reconnect: action((url?: string) => {
    socketService.reconnect(url);
  }),

  setUrl: action((url: string) => {
    socketService.setUrl(url);
  }),

  getUrl: (): string => {
    return socketService.getUrl();
  },

  sendMessage: action((contentText: string, sender: string, type?: string) => {
    if (socketStore.isConnected) {
      const payload = {
        content: { messageText: contentText }, // ← оборачиваем строку в ContentType
        sender,
        type,
      };
      socketService.emit('message:send', payload);
    }
  }),
  clearLogs: action(() => {
    socketStore.logs = [];
  }),

  cleanup: action(() => {
    socketService.off('connection:status', socketEventCallbacks.onConnectionStatus);
    socketService.off('connection:error', socketEventCallbacks.onConnectionError);
    socketService.off('push', socketEventCallbacks.onPushReceived);
    // socketService.off('hello', socketEventCallbacks.onHelloReceived);
    socketService.off('event', socketEventCallbacks.onEventReceived);
  }),
};